import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowRight, Search } from '@mui/icons-material';
import { endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subDays, subMonths, subWeeks } from 'date-fns';
import { reportService } from '../services/reportService';
import { formatCurrency } from '../components/CurrencyTextField';
import ReportExportMenu from '../components/ReportExportMenu';

const ymd = (d) => format(d, 'yyyy-MM-dd');
const WEEK = { weekStartsOn: 0 };

const PRESETS = [
  { label: 'This Week', range: () => [startOfWeek(new Date(), WEEK), endOfWeek(new Date(), WEEK)] },
  { label: 'Last Week', range: () => [startOfWeek(subWeeks(new Date(), 1), WEEK), endOfWeek(subWeeks(new Date(), 1), WEEK)] },
  { label: 'Last 30 Days', range: () => [subDays(new Date(), 29), new Date()] },
  { label: 'This Month', range: () => [startOfMonth(new Date()), endOfMonth(new Date())] },
  { label: 'Last Month', range: () => [startOfMonth(subMonths(new Date(), 1)), endOfMonth(subMonths(new Date(), 1))] },
  { label: 'All Time', range: () => [null, null] },
];

const h = (v) => (Number(v) || 0).toFixed(2);

function varianceCell(variance, pct) {
  if (variance == null) return <Typography variant="body2" color="text.secondary">—</Typography>;
  const over = variance > 0;
  return (
    <Typography variant="body2" sx={{ color: over ? 'error.main' : 'success.main', fontWeight: 600 }}>
      {over ? '+' : ''}{h(variance)}
      {pct != null ? <Typography component="span" variant="caption" color="text.secondary"> ({pct}%)</Typography> : null}
    </Typography>
  );
}

function SummaryCard({ title, hours, cost, caption, pct }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: '1 1 180px', minWidth: 180 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{h(hours)} h</Typography>
      <Typography variant="body2" color="text.secondary">
        {formatCurrency(cost)}{pct != null ? ` · ${pct}% of paid hours` : ''}
      </Typography>
      {caption ? <Typography variant="caption" color="text.secondary">{caption}</Typography> : null}
    </Paper>
  );
}

function OrderDetail({ order, dateFrom, dateTo, allTime }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    reportService
      .getLaborOrderDetail(order.work_order_id, { date_from: dateFrom || undefined, date_to: dateTo || undefined })
      .then((res) => !cancelled && setData(res.data))
      .catch((err) => !cancelled && setError(err.response?.data?.message || 'Failed to load detail'));
    return () => {
      cancelled = true;
    };
  }, [order.work_order_id, dateFrom, dateTo]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <LinearProgress sx={{ my: 1 }} />;

  const cellSx = { py: 0.5 };
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, py: 1.5 }}>
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>By function</Typography>
        {!data.has_function_estimates && data.estimated_hours != null ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
            This order has a total estimate ({h(data.estimated_hours)} h) but no per-function breakdown.
          </Typography>
        ) : null}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cellSx}>Function</TableCell>
              {!allTime && <TableCell sx={cellSx} align="right">Period h</TableCell>}
              <TableCell sx={cellSx} align="right">To date h</TableCell>
              <TableCell sx={cellSx} align="right">Estimate</TableCell>
              <TableCell sx={cellSx} align="right">Variance</TableCell>
              <TableCell sx={cellSx} align="right">Cost to date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_function.map((f) => (
              <TableRow key={f.work_function_id ?? 'none'}>
                <TableCell sx={cellSx}>{f.label}</TableCell>
                {!allTime && <TableCell sx={cellSx} align="right">{h(f.hours)}</TableCell>}
                <TableCell sx={cellSx} align="right">{h(f.hours_to_date)}</TableCell>
                <TableCell sx={cellSx} align="right">{f.estimated_hours != null ? h(f.estimated_hours) : '—'}</TableCell>
                <TableCell sx={cellSx} align="right">{varianceCell(f.variance_hours)}</TableCell>
                <TableCell sx={cellSx} align="right">{formatCurrency(f.cost_to_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 2, mb: 0.5 }}>By task</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cellSx}>Task</TableCell>
              {!allTime && <TableCell sx={cellSx} align="right">Period h</TableCell>}
              <TableCell sx={cellSx} align="right">To date h</TableCell>
              <TableCell sx={cellSx} align="right">Cost to date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_task.map((t) => (
              <TableRow key={t.shop_task_id ?? 'legacy'}>
                <TableCell sx={cellSx}>
                  {t.shop_task_id ? (
                    <Link component={RouterLink} to={`/shop-tasks/${t.shop_task_id}`}>{t.label}</Link>
                  ) : (
                    t.label
                  )}
                </TableCell>
                {!allTime && <TableCell sx={cellSx} align="right">{h(t.hours)}</TableCell>}
                <TableCell sx={cellSx} align="right">{h(t.hours_to_date)}</TableCell>
                <TableCell sx={cellSx} align="right">{formatCurrency(t.cost_to_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>By employee</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={cellSx}>Employee</TableCell>
              {!allTime && <TableCell sx={cellSx} align="right">Period h</TableCell>}
              {!allTime && <TableCell sx={cellSx} align="right">Period cost</TableCell>}
              <TableCell sx={cellSx} align="right">To date h</TableCell>
              <TableCell sx={cellSx} align="right">Cost to date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.by_employee.map((e) => {
              const noRate = e.unrated_hours_to_date > 0 && e.unrated_hours_to_date >= e.hours_to_date;
              return (
              <TableRow key={e.employee_id}>
                <TableCell sx={cellSx}>
                  {e.name}
                  {e.unrated_hours_to_date > 0 ? (
                    <Chip size="small" label="No pay rate" color="warning" variant="outlined" sx={{ ml: 1, height: 18 }} />
                  ) : null}
                </TableCell>
                {!allTime && <TableCell sx={cellSx} align="right">{h(e.hours)}</TableCell>}
                {!allTime && <TableCell sx={cellSx} align="right">{noRate ? '—' : formatCurrency(e.cost)}</TableCell>}
                <TableCell sx={cellSx} align="right">{h(e.hours_to_date)}</TableCell>
                <TableCell sx={cellSx} align="right">{noRate ? '—' : formatCurrency(e.cost_to_date)}</TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

const LaborByOrderReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(() => searchParams.get('from') ?? ymd(startOfMonth(new Date())));
  const [dateTo, setDateTo] = useState(() => searchParams.get('to') ?? ymd(endOfMonth(new Date())));
  const [status, setStatus] = useState(searchParams.get('status') || 'all');
  const [search, setSearch] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await reportService.getLaborByOrder({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        status,
      });
      setData(res.data);
      setExpanded(new Set());
      setSearchParams({ from: dateFrom || '', to: dateTo || '', ...(status !== 'all' ? { status } : {}) }, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, status, setSearchParams]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPreset = (p) => {
    const [from, to] = p.range();
    setDateFrom(from ? ymd(from) : '');
    setDateTo(to ? ymd(to) : '');
  };

  const allTime = Boolean(data?.all_time);
  const orders = useMemo(() => {
    const list = data?.orders || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((o) => `${o.identifier} ${o.customer_name || ''}`.toLowerCase().includes(q));
  }, [data, search]);

  const totals = useMemo(
    () =>
      orders.reduce(
        (acc, o) => ({
          hours: acc.hours + o.hours,
          cost: acc.cost + o.cost,
          hours_to_date: acc.hours_to_date + o.hours_to_date,
          cost_to_date: acc.cost_to_date + o.cost_to_date,
        }),
        { hours: 0, cost: 0, hours_to_date: 0, cost_to_date: 0 }
      ),
    [orders]
  );

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rangeLabel = allTime ? 'All time' : `${data?.date_from || '…'} – ${data?.date_to || '…'}`;

  const getExportData = useCallback(() => {
    if (!orders.length) return null;
    const headers = [
      'Order', 'Customer', 'Status',
      ...(allTime ? [] : ['Period hrs', 'Period cost']),
      'Hrs to date', 'Estimate', 'Variance', '% of est', 'Cost to date',
    ];
    const rows = orders.map((o) => [
      o.identifier,
      o.customer_name || '',
      o.status || '',
      ...(allTime ? [] : [h(o.hours), formatCurrency(o.cost)]),
      h(o.hours_to_date),
      o.estimated_hours != null ? h(o.estimated_hours) : '',
      o.variance_hours != null ? h(o.variance_hours) : '',
      o.pct_of_estimate != null ? `${o.pct_of_estimate}%` : '',
      formatCurrency(o.cost_to_date),
    ]);
    const footer = [
      '', '', 'Totals',
      ...(allTime ? [] : [h(totals.hours), formatCurrency(totals.cost)]),
      h(totals.hours_to_date), '', '', '', formatCurrency(totals.cost_to_date),
    ];
    return {
      headers,
      rows,
      footer,
      pdfTitle: `Labor by Work Order · ${rangeLabel}`,
      filenameBase: `labor-by-order_${allTime ? 'all-time' : `${data?.date_from}_${data?.date_to}`}`,
      pdfLandscape: true,
    };
  }, [orders, totals, allTime, rangeLabel, data]);

  const s = data?.summary;
  const colCount = allTime ? 9 : 11;

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 0.5 }}>
        Labor by Work Order
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Shop Task hours per order, costed at each employee's regular pay rate on the day worked (no overtime
        premium or burden). Estimate comparisons always use hours to date.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">Date presets:</Typography>
          {PRESETS.map((p) => (
            <Chip key={p.label} label={p.label} size="small" onClick={() => applyPreset(p)} sx={{ cursor: 'pointer' }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="Date From" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <TextField size="small" label="Date To" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
          <FormControl size="small" sx={{ width: 170 }}>
            <InputLabel>Orders</InputLabel>
            <Select label="Orders" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="all">All orders</MenuItem>
              <MenuItem value="active">Active only</MenuItem>
              <MenuItem value="closed">Shipped / cancelled</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" size="small" startIcon={<Search />} onClick={load} disabled={loading}>
            {loading ? 'Loading…' : 'Run report'}
          </Button>
          <ReportExportMenu disabled={loading || !orders.length} getData={getExportData} defaultFilenameBase="labor-by-order" />
          <TextField size="small" label="Filter orders" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ ml: 'auto', width: 220 }} />
        </Box>
      </Paper>

      {s && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <SummaryCard title="Paid hours" hours={s.paid_hours} cost={s.paid_cost} caption={rangeLabel} />
            <SummaryCard title="On work orders" hours={s.orders.hours} cost={s.orders.cost} pct={s.pct_on_orders} />
            <SummaryCard title="Other tasks (no order)" hours={s.other_tasks.hours} cost={s.other_tasks.cost} />
            <SummaryCard title="General Warehouse" hours={s.general.hours} cost={s.general.cost} caption="Not attributed to a task" />
          </Box>
          {s.unrated_hours > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {h(s.unrated_hours)} h were worked by employees with no pay rate on file, so they're counted in hours but
              not cost. Add pay rates under Human Resources → Employees.
            </Alert>
          )}
        </>
      )}

      <Paper sx={{ overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Order</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                {!allTime && <TableCell align="right">Period hrs</TableCell>}
                {!allTime && <TableCell align="right">Period cost</TableCell>}
                <TableCell align="right">Hrs to date</TableCell>
                <TableCell align="right">Estimate</TableCell>
                <TableCell align="right">Variance</TableCell>
                <TableCell align="right">Cost to date</TableCell>
                <TableCell align="right">People</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !data ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No hours were logged to work order tasks in this range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((o) => {
                  const open = expanded.has(o.work_order_id);
                  return (
                    <React.Fragment key={o.work_order_id}>
                      <TableRow hover sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined } }} onClick={() => toggle(o.work_order_id)}>
                        <TableCell padding="checkbox">
                          <IconButton size="small">{open ? <KeyboardArrowDown /> : <KeyboardArrowRight />}</IconButton>
                        </TableCell>
                        <TableCell>
                          <Link component={RouterLink} to={`/work-orders/${o.work_order_id}`} onClick={(e) => e.stopPropagation()} sx={{ fontWeight: 600 }}>
                            {o.identifier}
                          </Link>
                          {o.legacy_hours > 0 ? (
                            <Typography variant="caption" color="text.secondary" display="block">
                              incl. {h(o.legacy_hours)} h from old clock-out tags
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>{o.customer_name || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={o.status || '—'} variant="outlined" color={o.is_closed ? 'default' : 'primary'} />
                        </TableCell>
                        {!allTime && <TableCell align="right">{h(o.hours)}</TableCell>}
                        {!allTime && <TableCell align="right">{formatCurrency(o.cost)}</TableCell>}
                        <TableCell align="right">{h(o.hours_to_date)}</TableCell>
                        <TableCell align="right">{o.estimated_hours != null ? h(o.estimated_hours) : '—'}</TableCell>
                        <TableCell align="right">{varianceCell(o.variance_hours, o.pct_of_estimate)}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(o.cost_to_date)}
                          {o.unrated_hours_to_date > 0 ? (
                            <Typography variant="caption" color="warning.main" display="block">
                              {h(o.unrated_hours_to_date)} h unrated
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell align="right">{o.people}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={colCount} sx={{ py: 0, bgcolor: open ? 'action.hover' : undefined }}>
                          <Collapse in={open} timeout="auto" unmountOnExit>
                            <OrderDetail order={o} dateFrom={data.date_from} dateTo={data.date_to} allTime={allTime} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {orders.length > 0 && (
          <Box sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 3, flexWrap: 'wrap' }}>
            {!allTime && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Period: {h(totals.hours)} h · {formatCurrency(totals.cost)}
              </Typography>
            )}
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              To date: {h(totals.hours_to_date)} h · {formatCurrency(totals.cost_to_date)}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default LaborByOrderReport;
